import colors from "assets/styles/colors"
import React, { useEffect, useRef, useState, useContext } from "react"
import styled from "styled-components"
import BGGrid from "components/uIElements/BGGrid"
import gsap from "gsap"
import { CustomEase } from "gsap/CustomEase"
import text from "assets/styles/text"
import media from "assets/styles/media"
import { ReactComponent as GihpyLogoSVG } from "assets/images/homepage/giphyLogo.svg"
import { ReactComponent as VenmoSVG } from "assets/images/homepage/venmo.svg"
import { ReactComponent as DataDogSVG } from "assets/images/homepage/datadog.svg"
import binance from "assets/images/homepage/binance.png"
import { ReactComponent as NoomSVG } from "assets/images/homepage/noom.svg"
import { ReactComponent as BuzzFeedSVG } from "assets/images/homepage/buzzFeed.svg"
import { ReactComponent as RXDefineSVG } from "assets/images/homepage/rxdefine.svg"
import { ReactComponent as BrainTreeSVG } from "assets/images/homepage/brainTree.svg"
import dcg from "assets/images/homepage/dcg.png"
import { ReactComponent as OLOSVG } from "assets/images/homepage/olo.svg"
import { ReactComponent as TiveSVG } from "assets/images/homepage/tive.svg"
import { ReactComponent as RedoxSVG } from "assets/images/homepage/redox.svg"

import { ScreenContext } from "components/Providers"
import { useMedia } from "utils/hooks"

const WholeTime: React.FC<{}> = () => {
  const column1Ref = useRef<HTMLDivElement>(null)
  const column2Ref = useRef<HTMLDivElement>(null)
  const logos = useRef<HTMLDivElement>(null)
  const [wrapper, setWrapper] = useState<HTMLElement | null>(null)
  const innerWrapper = useRef<HTMLDivElement>(null)
  const { tablet, mobile } = useContext(ScreenContext)
  const wheelStart = useMedia(
    "top bottom",
    "top bottom",
    "top bottom",
    "bottom 190%"
  )
  const wheelStop = useMedia(
    "bottom 90%",
    "bottom 90%",
    "bottom 90%",
    "bottom 50%"
  )
  const innerStart = useMedia("top 20%", "top 20%", "top 20%", "top 50%")
  const innerStop = useMedia(
    "bottom 80%",
    "bottom 80%",
    "bottom 80%",
    "bottom 90%"
  )
  const innerY = useMedia("200vh", "200vh", "200vh", "100vh")

  const column1 = [
    <Venmo key="whole-time-venmo" />,
    <DataDog key="whole-time-DataDog" />,
    <Binance key="whole-time-Binance" src={binance} alt={"binance logo"} />,
    <Noom key="whole-time-Noom" />,
    <BuzzFeed key="whole-time-BuzzFeed" />,
    <RXDefine key="whole-time-RXDefine" />,
  ]

  const column2 = [
    <BrainTree key="whole-time-BrainTree" />,
    <DCG key="whole-time-DCG" src={dcg} alt={"bcg logo"} />,
    <OLO key="whole-time-OLO" />,
    <GiphyLogo key="whole-time-GiphyLogo" />,
    <Tive key="whole-time-Tive" />,
    <Redox key="whole-time-Redox" />,
  ]

  const gridConfig = {
    sectionHeight: "100%",
    sectionRef: wrapper,
    animationClass: "whole_time",
  }

  useEffect(() => {
    if (column1Ref.current && column2Ref.current && wrapper && logos.current) {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: wrapper,
          scroller: "#___gatsby",
          start: wheelStart,
          end: wheelStop,
          scrub: true,
          // markers: true,
        },
        ease: CustomEase.create("custom", "M0,0 C0.25,0 0.75,1 1,1 "),
      })

      tl.to(column1Ref.current, { y: "-80%", duration: 10 }, 0).to(
        column2Ref.current,
        { y: "80%", duration: 10 },
        0
      )

      return () => {
        tl.kill()
      }
    }
  }, [wrapper, wheelStart, wheelStop])

  useEffect(() => {
    if (innerWrapper.current && wrapper) {
      //sticky scroll

      gsap.fromTo(
        innerWrapper.current,
        {
          y: "0",
        },
        {
          y: innerY,
          ease: "linear",
          scrollTrigger: {
            trigger: wrapper,
            scrub: true,
            scroller: "#___gatsby",
            start: innerStart,
            end: innerStop,
          },
        }
      )
    }
  }, [mobile, wrapper, innerStop, innerStart, innerY])

  return (
    <Wrapper
      id="WholeTime"
      data-scroll
      data-scroll-section
      ref={ref => setWrapper(ref)}
    >
      <BGGrid lineConfig={gridConfig} />
      <InnerWrapper ref={innerWrapper}>
        {tablet ? (
          <Title>
            We've been here
            <span>the whole time.</span>
          </Title>
        ) : (
          <Title>
            We've been here the
            <span> whole time.</span>
          </Title>
        )}

        <Text>
          Supporting the movers and shakers of the world&#8212;the people
          building the best technology companies on the planet.
        </Text>
        <Logos ref={logos}>
          <Cover />
          <Inner>
            <Column ref={column1Ref}>{column1}</Column>
            <Column2 ref={column2Ref}>{column2}</Column2>
          </Inner>
          <CoverBottom />
        </Logos>
      </InnerWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  background: ${colors.blue400};
  padding: 17.36vw 3.47vw 20.83vw 12.99vw;
  height: 300vh;
  position: relative;
  will-change: transform;
  ${media.tablet} {
    height: 300vh;
    padding: 0;
  }
  ${media.mobile} {
    height: 200vh;
    padding: 0 3.73vw;
  }
`

const InnerWrapper = styled.div`
  position: absolute;
  top: 10vw;
  width: 83.54vw;
  height: 35.28vw;
  padding-bottom: 20.83vw;
  color: ${colors.white};
  transform: translate3d(0px, 0px, 0px);
  will-change: transform;
  ${media.tablet} {
    height: 100vh;
    display: flex;
    flex-direction: column;

    top: 0;
    padding: 14.65vw 12.99vw 17.48vw 4.88vw;
    width: 100%;
  }
  ${media.mobile} {
    height: 100vh;
    display: flex;
    flex-direction: column;

    top: 0;
    padding: 26.67vw 0;
    width: 92vw;
  }
`

const Title = styled.div`
  ${text.desktop.h3};
  width: 45.69vw;

  span {
    ${text.desktop.d2Point5};
    margin: 0;
    padding: 0;
  }
  ${media.tablet} {
    width: 87.7vw;
    ${text.tablet.h3};
    span {
      ${text.tablet.d2Point5};
    }
  }
  ${media.mobile} {
    font-size: 16vw;
    line-height: 15.87vw;
    letter-spacing: -2%;
    width: 100%;
    span {
      font-size: 24vw;
    }
  }
`

const Logos = styled.div`
  position: absolute;
  left: 46.3vw;
  top: 0;
  width: 36.18vw;
  height: 34.72vw;
  overflow: hidden;

  ${media.tablet} {
    position: relative;
    width: 74.12vw;
    height: 57.03vw;
    margin-top: 9.77vw;
    left: auto;
    top: auto;
    margin-left: 8.01vw;
  }

  ${media.mobile} {
    margin-top: 16vw;
    position: relative;
    width: 100%;
    height: 70.667vw;
    left: auto;
    top: auto;
  }
`

const Inner = styled.div`
  position: relative;
  display: flex;
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 15.97vw;
  margin-right: 2.6vw;
  margin-top: 50%;
  ${media.tablet} {
    width: 28.42vw;
  }
  ${media.mobile} {
    width: 35.2vw;
  }
`

const Column2 = styled(Column)`
  margin-right: 0;
  margin-top: 0;
  position: absolute;
  left: 18.5vw;
  bottom: auto;
  top: -50%;
  ${media.tablet} {
    left: 38.67vw;
  }
  ${media.mobile} {
    left: 52vw;
  }
`

const Cover = styled.div`
  position: absolute;
  width: 100%;
  height: 6.94vw;
  left: 0;
  top: 0;

  background: linear-gradient(180deg, rgba(25, 101, 254, 0) 0%, #1965fe 50%);
  z-index: 2;
  transform: rotate(180deg);
  ${media.tablet} {
  }
  ${media.mobile} {
    height: 5.333vw;
  }
`

const CoverBottom = styled(Cover)`
  top: auto;
  bottom: 0;
  left: 0;
  background: linear-gradient(180deg, rgba(24, 97, 253, 0) 0%, #1965fe 50%);
  transform: rotate(0);
`
const GiphyLogo = styled(GihpyLogoSVG)`
  width: 100%;
  height: auto;
  margin: 2.78vw 0;
  opacity: 0.2;
  ${media.tablet} {
    margin: 5.5vw auto;
  }
  ${media.mobile} {
    margin: 4vw 0;
  }
`
const Venmo = styled(VenmoSVG)`
  width: 100%;
  height: auto;
  margin: 2.78vw 0;
  opacity: 0.2;
  ${media.tablet} {
    margin: 5.5vw auto;
  }
  ${media.mobile} {
    margin: 4vw 0;
  }
`
const DataDog = styled(DataDogSVG)`
  width: 100%;
  height: auto;
  margin: 2.78vw 0;
  opacity: 0.2;
  ${media.tablet} {
    margin: 5.5vw auto;
  }
  ${media.mobile} {
    margin: 4vw 0;
  }
`
const Binance = styled.img`
  width: 100%;
  height: auto;
  margin: 2.78vw 0;
  opacity: 0.2;
  ${media.tablet} {
    margin: 5.5vw auto;
  }
  ${media.mobile} {
    margin: 4vw 0;
  }
`
const Noom = styled(NoomSVG)`
  width: 100%;
  height: auto;
  margin: 2.78vw 0;
  opacity: 0.2;
  ${media.tablet} {
    margin: 5.5vw auto;
  }
  ${media.mobile} {
    margin: 4vw 0;
  }
`
const BuzzFeed = styled(BuzzFeedSVG)`
  width: 100%;
  height: auto;
  margin: 2.78vw 0;
  opacity: 0.2;
  ${media.tablet} {
    margin: 5.5vw auto;
  }
  ${media.mobile} {
    margin: 4vw 0;
  }
`
const RXDefine = styled(RXDefineSVG)`
  width: 100%;
  height: auto;
  margin: 2.78vw 0;
  opacity: 0.2;
  ${media.tablet} {
    margin: 5.5vw auto;
  }
  ${media.mobile} {
    margin: 4vw 0;
  }
`
const BrainTree = styled(BrainTreeSVG)`
  width: 100%;
  height: auto;
  margin: 2.78vw 0;
  opacity: 0.2;
  ${media.tablet} {
    margin: 5.5vw auto;
  }
  ${media.mobile} {
    margin: 4vw 0;
  }
`
const DCG = styled.img`
  width: 100%;
  height: auto;
  margin: 2.78vw 0;
  opacity: 0.2;
  ${media.tablet} {
    margin: 5.5vw auto;
  }
  ${media.mobile} {
    margin: 4vw 0;
  }
`
const OLO = styled(OLOSVG)`
  width: 100%;
  height: auto;
  margin: 2.78vw 0;
  opacity: 0.2;
  ${media.tablet} {
    margin: 5.5vw auto;
  }
  ${media.mobile} {
    margin: 4vw 0;
  }
`
const Tive = styled(TiveSVG)`
  width: 100%;
  height: auto;
  margin: 2.78vw 0;
  opacity: 0.2;
  ${media.tablet} {
    margin: 5.5vw auto;
  }
  ${media.mobile} {
    margin: 4vw 0;
  }
`
const Redox = styled(RedoxSVG)`
  width: 100%;
  height: auto;
  margin: 2.78vw 0;
  opacity: 0.2;
  ${media.tablet} {
    margin: 5.5vw auto;
  }
  ${media.mobile} {
    margin: 4vw 0;
  }
`

const Text = styled.p`
  ${text.desktop.h5};
  width: 35.97vw;
  margin-top: 1.39vw;

  color: ${colors.blue50};
  ${media.tablet} {
    ${text.tablet.h5};
    width: 74.12vw;
  }
  ${media.mobile} {
    width: 100%;
    ${text.mobile.bodyL};
  }
`

export default WholeTime
